<template>
  <div class="app-container">
    <!-- KPIs -->
    <ManagementControlCards 
      :driverCount="driverCount" 
      :averagePackages="averagePackages"
      :statusStats="statusStats"
    />
    <div class="app-view-card">
      <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div class="d-flex align-items-center flex-wrap gap-2">
          <h3 class="mb-0 text-primary fw-bold">
            {{ currentMode === 'control-gestion' ? 'Control de Gestión' : 'Nivel de Cumplimiento' }}
          </h3>
        </div>

        <b-dropdown 
          v-if="currentMode === 'nivel-cumplimiento' && $debug.enabled" 
          text="Acciones" 
          variant="primary" 
          right
        >
          <b-dropdown-item @click="openModal('generateSettlements')">
            Generar preliquidaciones
          </b-dropdown-item>
          <b-dropdown-item @click="openModal('driverCounts')">
            Subir registros de conductores
          </b-dropdown-item>
          <b-dropdown-item @click="openModal('shipments')">
            Subir registros de envíos
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <!-- Filtros -->
      <ManagementControlFilters 
        :currentMode="currentMode" 
        @update:currentMode="setMode"
        @filter="applyFilters" 
      />

      <!-- Tabla -->
      <div class="mt-3">
      <div class="last-updated text-muted small mb-1">
        Actualizado {{ lastUpdatedLabel }}
      </div>
        <b-table 
          :items="items"
          :fields="fields"
          :busy="isBusy"
          responsive
          empty-text="No hay datos disponibles"
          show-empty
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>Cargando...</strong>
            </div>
          </template>

          <!-- Conductor: código + nombre (Solo para Nivel de Cumplimiento) -->
          <template v-if="currentMode === 'nivel-cumplimiento'" #cell(driverCode)="{ item }">
            <span v-if="item.driverCode">{{ item.driverCode }}</span>
            <span v-else>-</span>
          </template>
          <template v-if="currentMode === 'nivel-cumplimiento'" #cell(driverName)="{ item }">
            {{ item.driverName }}
          </template>
        </b-table>
      </div>
    </div>

    <ModalImportDriverCounts v-model="modals.driverCounts" @success="onImportSuccess" />
    <ModalImportShipments v-model="modals.shipments" @success="onImportSuccess" />
    <ModalGenerateSettlements v-model="modals.generateSettlements" @success="onImportSuccess" />
  </div>
</template>

<script>
import ManagementControlFilters from '../components/managementControl/ManagementControlFilters.vue'
import ManagementControlCards from '../components/managementControl/ManagementControlCards.vue'
import ModalImportDriverCounts from '../components/managementControl/ModalImportDriverCounts.vue'
import ModalImportShipments from '../components/managementControl/ModalImportShipments.vue'
import ModalGenerateSettlements from '../components/managementControl/ModalGenerateSettlements.vue'
import { mapActions } from 'vuex'
import useAutoRefresh from '../services/useAutoRefresh'
import humanizeRelativeTime from '../services/humanizeRelativeTime'

export default {
  name: 'ManagementControlView',
  components: {
    ManagementControlFilters,
    ManagementControlCards,
    ModalImportDriverCounts,
    ModalImportShipments,
    ModalGenerateSettlements
  },

  computed: {
    validDrivers() {
      if (this.currentMode === 'control-gestion') {
        return this.items.filter(item => item.name !== 'Sin Repartidor' && item.name !== 'Sin Conductor')
      } else {
        return this.items.filter(item => item.driverName && item.driverName !== 'Sin Conductor')
      }
    },
    driverCount() {
      if (this.currentMode === 'control-gestion') {
        return this.validDrivers.length
      } else {
        // Conductores únicos en los resultados
        const ids = new Set(this.items.map(i => i.driverId).filter(Boolean))
        return ids.size
      }
    },
    averagePackages() {
      if (this.driverCount === 0) return 0
      if (this.currentMode === 'control-gestion') {
        const totalPackages = this.validDrivers.reduce((sum, item) => sum + (item.total || 0), 0)
        return Math.round((totalPackages / this.driverCount) * 10) / 10
      } else {
        const totalPackages = this.items.reduce((sum, item) => sum + (item.totalCount || 0), 0)
        return Math.round((totalPackages / this.items.length) * 10) / 10
      }
    },
    statusStats() {
      if (this.currentMode === 'control-gestion') {
        const keys = ['in-transit', 'approved', 'partial', 'rescheduled', 'rejected', 'cancelled']
        const totals = {}
        let grand = 0
        keys.forEach(k => {
          const sum = this.items.reduce((acc, item) => acc + (Number(item[k]) || 0), 0)
          totals[k] = sum
          grand += sum
        })
        const result = {}
        keys.forEach(k => {
          result[k] = {
            count: totals[k],
            percentage: grand > 0 ? Math.round((totals[k] / grand) * 1000) / 10 : 0
          }
        })
        result.total = grand
        return result
      } else {
        const keys = ['inTransitCount', 'approvedCount', 'partialCount', 'rescheduledCount', 'rejectedCount', 'cancelledCount']
        const labelMap = {
          inTransitCount:  'in-transit',
          approvedCount:   'approved',
          partialCount:    'partial',
          rescheduledCount: 'rescheduled',
          rejectedCount:   'rejected',
          cancelledCount:  'cancelled',
        }
        const totals = {}
        let grand = 0
        keys.forEach(k => {
          const sum = this.items.reduce((acc, item) => acc + (Number(item[k]) || 0), 0)
          totals[labelMap[k]] = sum
          grand += sum
        })
        const result = {}
        Object.keys(totals).forEach(k => {
          result[k] = {
            count: totals[k],
            percentage: grand > 0 ? Math.round((totals[k] / grand) * 1000) / 10 : 0
          }
        })
        result.total = grand
        return result
      }
    },
    fields() {
      return this.currentMode === 'control-gestion'
        ? this.fieldsControlGestion
        : this.fieldsNivelCumplimiento
    }
  },

  data() {
    return {
      currentMode: 'control-gestion',
      fieldsControlGestion: [
        { key: 'number', label: 'N°' },
        { key: 'code', label: 'Cod. Repartidor' },
        { key: 'name', label: 'Repartidor' },
        { key: 'in-transit', label: 'En Transito' },
        { key: 'approved', label: 'Entregados' },
        { key: 'partial', label: 'Pendientes' },
        { key: 'rescheduled', label: 'Reprogramados' },
        { key: 'rejected', label: 'Rechazados' },
        { key: 'cancelled', label: 'Cancelados' },
        { key: 'total', label: 'Total' }
      ],
      fieldsNivelCumplimiento: [
        { key: 'driverCode',       label: 'Cód. Conductor', tdClass: 'text-center', thClass: 'text-center' },
        { key: 'driverName',       label: 'Conductor' },
        { key: 'inTransitCount',   label: 'En Tránsito',   tdClass: 'text-center', thClass: 'text-center' },
        { key: 'approvedCount',    label: 'Entregados',    tdClass: 'text-center', thClass: 'text-center' },
        { key: 'partialCount',     label: 'Pendientes',    tdClass: 'text-center', thClass: 'text-center' },
        { key: 'rescheduledCount', label: 'Reprogramados', tdClass: 'text-center', thClass: 'text-center' },
        { key: 'rejectedCount',    label: 'Rechazados',    tdClass: 'text-center', thClass: 'text-center' },
        { key: 'cancelledCount',   label: 'Cancelados',    tdClass: 'text-center', thClass: 'text-center' },
        { key: 'totalCount',       label: 'Total',         tdClass: 'text-center fw-bold', thClass: 'text-center' },
        { key: 'inTransitScanCount', label: 'Escaneos en Tránsito',      tdClass: 'text-center', thClass: 'text-center' },
      ],
      items: [],
      isBusy: false,
      modals: {
        driverCounts: false,
        shipments: false,
        generateSettlements: false
      },
      lastFilters: null,
      autoRefresh: null,
      lastUpdatedAt: new Date(),
      lastUpdatedLabel: 'hace unos segundos',
      lastUpdatedTimer: null
    }
  },

  methods: {
    ...mapActions('settlement', ['listSettlements']),
    ...mapActions(['getManagementControlStats']),

    openModal(which) {
      this.modals[which] = true
    },

    onImportSuccess() {
      if (this.lastFilters) {
        this.applyFilters(this.lastFilters)
      }
    },

    setMode(mode) {
      if (this.currentMode === mode) return
      this.currentMode = mode
      this.onModeChange()
    },

    onModeChange() {
      this.items = []
      if (this.lastFilters) {
        this.applyFilters(this.lastFilters)
      }
    },

    async applyFilters(filters) {
      if (!filters?.date && (!filters?.startDate || !filters?.endDate)) {
        this.$bvToast.toast('Debe seleccionar una fecha específica o un rango de fechas', {
          title: 'Aviso',
          variant: 'warning',
          solid: true
        })
        return
      }

      this.lastFilters = filters

      try {
        this.isBusy = true
        this.items = []

        if (this.currentMode === 'control-gestion') {
          const params = {}
          if (filters?.search)    params.search        = filters.search
          if (filters?.date)      params.date          = filters.date
          if (filters?.startDate) params.startDate     = filters.startDate
          if (filters?.endDate)   params.endDate       = filters.endDate
          if (filters?.isPickupRequest !== undefined && filters?.isPickupRequest !== null) {
            params.isPickupRequest = filters.isPickupRequest
          }

          const data = await this.getManagementControlStats({ params })
          this.items = data || []
        } else {
          const params = {}
          if (filters?.date)      params.operationDate = filters.date
          if (filters?.startDate) params.startDate     = filters.startDate
          if (filters?.endDate)   params.endDate       = filters.endDate
          if (filters?.driverId)  params.driverId      = filters.driverId
          if (filters?.isPickupRequest !== undefined && filters?.isPickupRequest !== null) {
            params.isPickupRequest = filters.isPickupRequest
          }

          const data = await this.listSettlements({ params })
          this.items = Array.isArray(data) ? data : (data?.rows || [])
        }
      } catch (error) {
        console.error(`Error al obtener datos (${this.currentMode}):`, error)
        this.items = []
        this.$bvToast.toast('Error al obtener datos', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isBusy = false
        this.markUpdated()
      }
    },

    markUpdated() {
      this.lastUpdatedAt = new Date()
      this.lastUpdatedLabel = humanizeRelativeTime(this.lastUpdatedAt)
    },

    updateLastUpdatedLabel() {
      this.lastUpdatedLabel = humanizeRelativeTime(this.lastUpdatedAt)
    }
  },

  mounted() {
    this.autoRefresh = useAutoRefresh({
      callback: () => this.lastFilters ? this.applyFilters(this.lastFilters) : Promise.resolve()
    })
    this.autoRefresh.start()
    this.lastUpdatedTimer = setInterval(this.updateLastUpdatedLabel, 10000)
  },

  beforeDestroy() {
    this.autoRefresh?.stop()
    clearInterval(this.lastUpdatedTimer)
  }
}
</script>

<style scoped>
.last-updated {
  text-align: right;
}

.management-control {
  min-height: calc(100vh - 100px);
  padding: 1.5rem;
}
.page-title {
  margin-bottom: 0.5rem;
}
.table-container {
  background-color: #fff;
  border: 1px solid rgba(0,0,0,.125);
  border-radius: 0.25rem;
  padding: 1.25rem;
  margin-top: 1.5rem;
}
</style>
